import * as S from './ServiceList.styles.js';
import { ServiceListItem } from './ServiceListItem.js';
import { type Services_userProfile$key } from '@/__generated__/relay/Services_userProfile.graphql.js';
import { Layout } from '@/components/Layout/Layout.js';
import { MobileServices } from '@/components/MobileServices/MobileServices.js';
import { FadeUpInView } from '@/components/Motion/Fade/FadeUpInView/FadeUpInView.js';
import { Section } from '@/components/Section/Section.js';
import { ServicesFragment } from '@/documents/fragments/Services.js';
import { useHeadlineVariant } from '@/hooks/useHeadlineVariant.js';
import { useIPFragment } from '@/hooks/useIPFragment.js';
import { useMatchMedia } from '@/hooks/useMatchMedia.js';
import { minWidthMediaQueries } from '@/stitches/media.js';
import { getVisibleServices } from '@/utilities/service/getVisibleServices.js';

export type ServiceListProps = {
  /**
   * When `ServiceList` is used in the service page we use this prop to hide the current open service in the `More Services` list
   */
  readonly currentServiceId?: string;
  readonly queryRef: Services_userProfile$key;
  readonly title: string;
};

export const ServiceList = ({
  currentServiceId,
  queryRef,
  title,
}: ServiceListProps) => {
  const headlineFontFamily = useHeadlineVariant();
  const isMediumViewport = useMatchMedia(minWidthMediaQueries.bp2);
  const result = useIPFragment<Services_userProfile$key>(
    ServicesFragment,
    queryRef,
  );
  if (!result) return null;

  const services = getVisibleServices(result.productizedServices);

  const servicesToUse = currentServiceId
    ? services.filter(({ id }) => id !== currentServiceId)
    : services;

  if (!servicesToUse.length) return null;

  return isMediumViewport ? (
    <Section
      css={{ paddingTop: '$80', zIndex: 1 }}
      id="services"
    >
      <Layout.Grid>
        <Layout.GridColumn initial={12}>
          <FadeUpInView>
            <S.StyledHeading
              as="h2"
              fontFamily={headlineFontFamily}
            >
              {title}
            </S.StyledHeading>
          </FadeUpInView>
          <S.ServiceListContainer>
            {servicesToUse.map((node, index) => (
              <ServiceListItem
                index={index}
                isLast={index === servicesToUse.length - 1}
                key={node.id}
                node={node}
              />
            ))}
          </S.ServiceListContainer>
        </Layout.GridColumn>
      </Layout.Grid>
    </Section>
  ) : (
    <MobileServices
      services={servicesToUse}
      uppercaseTrigger={false}
    />
  );
};
